import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiSearch, FiX, FiDownload, FiFileText, FiBookOpen, FiInbox,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import SearchableSelect from '../../components/SearchableSelect';
import { templateLibraryAPI, parametersAPI, companiesAPI } from '../../services/api';
import { getLocalized } from '../../i18n/getLocalized';
import { formatDate } from '../../utils/dateFormat';
import { saveBlob } from '../../utils/fileDownload';
import PageHeader from '../../components/PageHeader/PageHeader';
import './TemplateLibrary.scss';

// General Template Library (Sözleşme Kütüphanesi) — browse, filter and
// download the active standard contract templates, independent of any
// contract-creation flow. Reached from the sidebar, Navi and the dashboard's
// quick actions; management lives under Contract Settings.
function TemplateLibrary() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    templateLibraryAPI.list({ active: 'true' })
      .then(r => setItems(r.data.items || []))
      .catch(() => toast.error(t('templateLibrary.messages.loadFailed')))
      .finally(() => setLoading(false));
    companiesAPI.list({ active: 'true' })
      .then(r => setCompanies(r.data.companies || []))
      .catch(() => {});
    parametersAPI.contractTypes.list({ active: 'true' })
      .then(r => setContractTypes(r.data.items || []))
      .catch(() => {});
  }, [t]);

  // The type filter only offers types available to the selected company.
  const typeOptions = useMemo(() => {
    if (!companyFilter) return contractTypes;
    return contractTypes.filter(ct => {
      const ids = (ct.companyIds || []).map(String);
      return ids.length === 0 || ids.includes(String(companyFilter));
    });
  }, [contractTypes, companyFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(item => {
      if (companyFilter && String(item.companyId?._id) !== String(companyFilter)) return false;
      if (typeFilter && String(item.contractTypeId?._id) !== String(typeFilter)) return false;
      if (!q) return true;
      return (item.name || '').toLowerCase().includes(q)
        || (item.fileId?.name || '').toLowerCase().includes(q)
        || (item.companyId?.name || '').toLowerCase().includes(q)
        || getLocalized(item.contractTypeId?.name, i18n.language).toLowerCase().includes(q);
    });
  }, [items, query, companyFilter, typeFilter, i18n.language]);

  const handleDownload = async (item) => {
    setDownloadingId(item._id);
    try {
      const res = await templateLibraryAPI.download(item._id);
      saveBlob(res.data, item.fileId?.name || item.name);
    } catch {
      toast.error(t('templateLibrary.messages.downloadFailed'));
    }
    setDownloadingId(null);
  };

  return (
    <div className="tlb">
      <PageHeader
        title={t('templateLibrary.title')}
        description={t('pageDescriptions.templateLibrary')}
      />

      <div className="tlb-toolbar">
        <div className="tlb-search">
          <FiSearch className="tlb-search__icon" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('templateLibrary.searchPlaceholder')}
          />
          {query && (
            <button className="tlb-search__clear" onClick={() => setQuery('')} aria-label="Clear">
              <FiX size={14} />
            </button>
          )}
        </div>
        <div className="tlb-filter">
          <SearchableSelect
            value={companyFilter}
            onChange={(v) => { setCompanyFilter(v); setTypeFilter(''); }}
            placeholder={t('templateLibrary.allCompanies')}
            clearable
            options={companies.map(c => ({ value: c._id, label: c.name }))}
          />
        </div>
        <div className="tlb-filter">
          <SearchableSelect
            value={typeFilter}
            onChange={setTypeFilter}
            placeholder={t('templateLibrary.allTypes')}
            clearable
            options={typeOptions.map(ct => ({ value: ct._id, label: getLocalized(ct.name, i18n.language) }))}
          />
        </div>
      </div>

      {loading ? (
        <div className="tlb-state">
          <div className="tlb-state__spinner" />
          <p>{t('common.loading')}</p>
        </div>
      ) : !filtered.length ? (
        <div className="tlb-state">
          <div className="tlb-state__icon">
            {query || companyFilter || typeFilter ? <FiInbox size={26} /> : <FiBookOpen size={26} />}
          </div>
          <h3>{t('templateLibrary.empty.title')}</h3>
          <p>
            {query || companyFilter || typeFilter
              ? t('templateLibrary.empty.filtered')
              : t('templateLibrary.empty.desc')}
          </p>
        </div>
      ) : (
        <div className="tlb-table-wrap">
          <table className="tlb-table">
            <thead>
              <tr>
                <th>{t('templateLibrary.columns.name')}</th>
                <th>{t('templateLibrary.columns.company')}</th>
                <th>{t('templateLibrary.columns.contractType')}</th>
                <th>{t('templateLibrary.columns.fileName')}</th>
                <th>{t('templateLibrary.columns.version')}</th>
                <th>{t('templateLibrary.columns.updated')}</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item._id}>
                  <td>
                    <span className="tlb-name">
                      <span className="tlb-name__icon"><FiFileText size={14} /></span>
                      <strong>{item.name}</strong>
                    </span>
                  </td>
                  <td>{item.companyId?.name || '—'}</td>
                  <td>{getLocalized(item.contractTypeId?.name, i18n.language) || '—'}</td>
                  <td className="tlb-file">{item.fileId?.name || '—'}</td>
                  <td><code>v{item.version || 1}</code></td>
                  <td>{formatDate(item.updatedAt)}</td>
                  <td>
                    <button
                      className="tlb-download"
                      onClick={() => handleDownload(item)}
                      disabled={downloadingId === item._id}
                      title={t('templateLibrary.download')}
                      aria-label={t('templateLibrary.download')}
                    >
                      <FiDownload size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TemplateLibrary;
